/**
 * Centralized frontend validation framework — mirrors backend validators.py.
 *
 * Usage:
 *   import { validators, validateForm } from '../utils/validation';
 *   const err = validators.name(value);            // string | null
 *   const { errors, isValid } = validateForm(values, {
 *     first_name: [validators.required, validators.name],
 *     email: [validators.required, validators.email],
 *   });
 */

const KEYBOARD_JUNK = new Set([
  'qwerty', 'qwertyuiop', 'asdf', 'asdfgh', 'asdfghjkl', 'zxcv', 'zxcvbnm',
  'qazwsx', 'wasd', 'hjkl', 'uiop', 'asdasd', 'testtest', 'qweqwe',
  'abcabc', 'xyzxyz', 'lorem', 'ipsum', 'abcdef', 'abcdefg',
]);

const NAME_RE = /^[A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ'’. -]*$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[\d\s\-().+]+$/;
const ALPHANUM_ID_RE = /^[A-Za-z0-9][A-Za-z0-9\-_./ ]*$/;

export const collapseWs = (v) => String(v ?? '').replace(/\s+/g, ' ').trim();

export function isMeaningful(value) {
  const compact = String(value ?? '').replace(/\s+/g, '').toLowerCase();
  if (!compact) return false;
  if (!/[a-zà-öø-ÿ]/i.test(compact)) return false;
  if (compact.length >= 3 && new Set(compact).size === 1) return false;
  if (compact.length >= 5) {
    const counts = {};
    for (const c of compact) counts[c] = (counts[c] || 0) + 1;
    const most = Math.max(...Object.values(counts));
    if (most / compact.length > 0.7) return false;
  }
  if (compact.length >= 6 && /^(.{1,4})\1{2,}$/.test(compact)) return false;
  const tokens = String(value).toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (tokens.length && tokens.every((t) => KEYBOARD_JUNK.has(t))) return false;
  return true;
}

const isEmpty = (v) => v === null || v === undefined || String(v).trim() === '';

function textRule(label, min, max, { pattern, patternMsg, meaningful = true } = {}) {
  return (v) => {
    if (isEmpty(v)) return null; // combine with validators.required for mandatory fields
    const val = collapseWs(v);
    if (val.length < min) return `${label} must be at least ${min} characters.`;
    if (val.length > max) return `${label} cannot exceed ${max} characters.`;
    if (pattern && !pattern.test(val)) return `${label} ${patternMsg || 'contains invalid characters.'}`;
    if (meaningful && !isMeaningful(val)) {
      return `Please enter a meaningful ${label.toLowerCase()} — repeated or random characters are not allowed.`;
    }
    return null;
  };
}

function numberRule(label, { gt, ge, le, integer = false } = {}) {
  return (v) => {
    if (isEmpty(v)) return null;
    const str = String(v).trim();
    if (!/^-?\d*\.?\d+$/.test(str)) return `${label} must be a number — letters and symbols are not allowed.`;
    const num = Number(str);
    if (integer && !Number.isInteger(num)) return `${label} must be a whole number.`;
    if (gt !== undefined && num <= gt) return `${label} must be greater than ${gt}.`;
    if (ge !== undefined && num < ge) return `${label} cannot be less than ${ge}.`;
    if (le !== undefined && num > le) return `${label} cannot exceed ${le}.`;
    return null;
  };
}

export const validators = {
  required: (v) => (isEmpty(v) ? 'This field is required.' : null),

  name: textRule('Name', 2, 50, {
    pattern: NAME_RE,
    patternMsg: 'may only contain letters, spaces, apostrophes and hyphens.',
  }),
  city: textRule('City', 2, 80, {
    pattern: NAME_RE,
    patternMsg: 'may only contain letters, spaces and hyphens.',
  }),
  companyName: textRule('Company name', 2, 150, {
    pattern: /^[A-Za-z0-9À-ÖØ-öø-ÿ'’&,.+ -]+$/,
    patternMsg: 'contains unsupported characters.',
  }),
  jobTitle: textRule('Job title', 3, 100),
  address: textRule('Address', 5, 255),
  description: textRule('Description', 10, 3000),
  bio: textRule('Bio', 2, 1000),
  meaningful: (v) =>
    isEmpty(v) || isMeaningful(v)
      ? null
      : 'Repeated or random characters are not allowed.',

  email: (v) => {
    if (isEmpty(v)) return null;
    return EMAIL_RE.test(String(v).trim()) ? null : 'Please enter a valid email address.';
  },

  phone: (v) => {
    if (isEmpty(v)) return null;
    const val = collapseWs(v);
    if (!PHONE_RE.test(val)) return 'Phone number may only contain digits, spaces, +, -, ( and ).';
    const digits = val.replace(/\D/g, '');
    if (digits.length < 7 || digits.length > 15) return 'Phone number must contain between 7 and 15 digits.';
    return null;
  },

  alphanumericId: textRule('ID', 2, 50, {
    pattern: ALPHANUM_ID_RE,
    patternMsg: 'may only contain letters, numbers, hyphens and underscores.',
    meaningful: false,
  }),

  strongPassword: (v) => {
    if (isEmpty(v)) return null;
    const val = String(v);
    if (val.length < 8) return 'Password must be at least 8 characters long.';
    if (val.length > 128) return 'Password cannot exceed 128 characters.';
    if (!/[A-Z]/.test(val)) return 'Password must contain at least one uppercase letter.';
    if (!/[a-z]/.test(val)) return 'Password must contain at least one lowercase letter.';
    if (!/\d/.test(val)) return 'Password must contain at least one number.';
    if (!/[^A-Za-z0-9]/.test(val)) return 'Password must contain at least one special character.';
    return null;
  },

  numeric: numberRule('This field'),
  currency: numberRule('Amount', { gt: 0, le: 1000000 }),
  rate: numberRule('Rate', { gt: 0, le: 10000 }),
  percent: numberRule('Percentage', { ge: 0, le: 100 }),
  nonNegative: numberRule('Value', { ge: 0 }),
  wholeNumber: numberRule('This field', { ge: 0, integer: true }),

  date: (v) => {
    if (isEmpty(v)) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? 'Invalid date.' : null;
  },

  maxLength: (max, label = 'This field') => (v) =>
    !isEmpty(v) && collapseWs(v).length > max ? `${label} cannot exceed ${max} characters.` : null,
  minLength: (min, label = 'This field') => (v) =>
    !isEmpty(v) && collapseWs(v).length < min ? `${label} must be at least ${min} characters.` : null,
};

/** End must be strictly after start. Returns error string or null. */
export function validateTimeRange(start, end, label = 'End time') {
  if (isEmpty(start) || isEmpty(end)) return null;
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 'Invalid date.';
  return e > s ? null : `${label} must be after the start time.`;
}

/** Passwords must match. */
export function validatePasswordMatch(password, confirm) {
  if (isEmpty(confirm)) return null;
  return password === confirm ? null : 'Passwords do not match.';
}

/**
 * Validate a whole form.
 * schema: { fieldName: [ruleFn, ruleFn, ...] }
 * Returns { errors: {field: msg}, isValid: bool }
 */
export function validateForm(values, schema) {
  const errors = {};
  for (const [field, rules] of Object.entries(schema)) {
    for (const rule of rules) {
      const err = rule(values[field], values);
      if (err) {
        errors[field] = err;
        break;
      }
    }
  }
  return { errors, isValid: Object.keys(errors).length === 0 };
}
